<template>
    <v-dialog v-model="dialog" persistent width="500">
        <template v-slot:activator="{ props }">
            <!-- No final report, no button: the only thing it could open is an
                 empty list. While the reports load there is none either, so it
                 appears once there is something to pick. -->
            <v-btn v-if="reports.length" v-bind="props" variant="text" color="accent" class="my-2">
                <v-icon start> mdi-text-box-check-outline </v-icon>
                {{ t("select_from_report") }}
            </v-btn>
        </template>
        <v-card :loading="loading ? 'accent' : false">
            <v-card-title>{{ t("select_from_final_report") }}</v-card-title>
            <v-card-subtitle class="text-wrap">{{ t("only_final_reports_listed") }}</v-card-subtitle>
            <v-card-text v-if="loading">
                <v-skeleton-loader type="list-item@3"></v-skeleton-loader>
            </v-card-text>
            <!-- Reachable only when the list empties with the dialog open
                 (a project switch): the activator is gone by then, but the
                 dialog is not, and a blank card looks broken. -->
            <v-card-text v-else-if="reports.length == 0">
                <v-alert variant="tonal" type="info" density="compact" class="text-caption">
                    <div>{{ t("no_final_reports") }}</div>
                    <div class="mt-1">{{ t("no_final_reports_hint") }}</div>
                </v-alert>
            </v-card-text>
            <v-card-text v-else>
                <v-list>
                    <v-list-item v-for="report in reports" :key="report.uuid" @click="select_report(report)">
                        <v-list-item-title>{{ report.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ report.document_type }}
                            (<Timestamp :model-value="report.modified"> </Timestamp>)
                        </v-list-item-subtitle>
                        <template v-slot:append>
                            <span class="text-caption text-medium-emphasis">
                                {{ constructible_count(report) }}
                            </span>
                        </template>
                    </v-list-item>
                </v-list>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="secondary" variant="text" @click="dialog = false">
                    {{ t("close") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>


<script setup>
import Timestamp from "@/components/renderers/Timestamp";
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { usePitStoreBasics } from '@/stores/app_common';
import { useReportsStore } from '@/stores/reports';
import { useAppStore } from "@/stores/app";
import bus from '/src/utils/event_bus';
import _ from "lodash"


const { t } = usePitStoreBasics();
const reports_store = useReportsStore()
const app_store = useAppStore()
const emit = defineEmits(["selected"])

const dialog = ref(false)

// The reports list is only loaded while something on screen shows it.
onMounted(() => reports_store.useReports())
onUnmounted(() => reports_store.releaseReports())
const loading = computed(() => app_store.loader_is_active("Reports"))

// Only final documents can be copied into the production view: a report that
// is still being worked on is not something to produce against.
const reports = computed(() => reports_store.reports.filter(r => r.document_status == "final"))

// Modules and assemblies only. Designs are deliberately left out — they are
// not units to produce, so there is nothing to select in the tree for them.
const constructibles_of = (report) => _.concat(
    report.selected_modules || [], report.selected_assemblies || [])

const constructible_count = (report) => constructibles_of(report).length

const select_report = (report) => {
    const uuids = constructibles_of(report)
    emit("selected", uuids)
    dialog.value = false
    bus.emit("notification", uuids.length
        ? { text: `${uuids.length} ${t("constructibles")} ${t("selection_loaded_from_report")}`, color: "success" }
        : { text: t("report_has_no_constructibles"), color: "warning" })
}

</script>